import DIContainer from '@components/DependencyInjectionContainer.js';
import { getID } from '@components/Utility/GeneralUtil.js';
import { TOKEN } from '@components/Environment.js';
import { DateTimeManager } from '@components/DateTimeManager/DateTimeManager.js';
import { htmlToPlaintext, plaintextToHTML } from '@components/Utility/StringUtil.js';
/**
 * Connects DOM elements to corresponding components
 * @param parentElement - The parent HTML element under which components will be searched for
 * @param components - An object with keys as component IDs and values as component instances
 */
export function connectDOM(parentElement, components) {
    // Query all elements with a data-componentid attribute
    const elements = parentElement.querySelectorAll('[data-componentid]');
    // Loop over the NodeList
    elements.forEach(element => {
        const componentID = element.getAttribute('data-componentid');
        if (components[componentID]) {
            components[componentID].rootElement = element;
            components[componentID].draw();
        }
    });
}
/**
 * Creates component instances from DOM elements.
 * For each DOM element with a 'data-componenttype' attribute, creates a new instance of the corresponding component type
 * and associates it with the DOM element. Component types are looked up in the global 's360' object.
 * @param parentElement - The parent HTML element under which component DOM elements will be searched for
 * @returns An object with keys as component IDs and values as component instances
 */
export function createComponentsFromDOM(parentElement) {
    const components = {};
    const s360 = window.s360;
    if (s360) {
        const elements = parentElement.querySelectorAll('[data-componenttype]');
        elements.forEach((element) => {
            const className = element.dataset.componenttype;
            if (className) {
                const id = element.dataset.componentid || getID(className);
                try {
                    const component = DIContainer.resolve(className, id, element);
                    component.rootElement = element;
                    components[id] = component;
                    component.draw();
                }
                catch (e) {
                    console.log(e);
                }
            }
        });
    }
    return components;
}
function parseConditionals(template, options, tokenValues) {
    if (!options.enableConditionals || template.indexOf("{{#if") === -1) {
        return template;
    }
    // Matches: {{#if token:modifier=compareWith}} or {{#elseif token:modifier!=compareWith}}
    // Supports ! prefix for negation: {{#if !token}} {{#if !perm:quote_view}}
    const parseCondTag = (tag) => {
        const m = /^{{#(?:if|elseif)\s+(!?)([^\s:=!}]+)\s*(?::\s*([^\s=!}:]+))?\s*(?:(!=|=)\s*([^\s}]+?))?\s*}}$/.exec(tag.trim());
        if (!m)
            return null;
        return { negated: m[1] === '!', tokenName: m[2], modifier: m[3], operator: m[4], compareWith: m[5] };
    };
    const conditionsManager = DIContainer.getInstance('ConditionsManager');
    const evalCondition = (negated, tokenName, modifier, operator, compareWith) => {
        let result;
        if (tokenName === 'perm') {
            result = modifier ? conditionsManager.checkPerm(modifier) : false;
        }
        else {
            let tokenValue = "";
            // TODO: this logic needs to better match the way we handle the tokens when actually parsing the template as well.
            if (tokenValues && Reflect.has(tokenValues, tokenName)) {
                tokenValue = tokenValues[tokenName];
            }
            else if (options.parseTokenCallback) {
                tokenValue = options.parseTokenCallback(tokenName, modifier || "") ?? "";
            }
            if (modifier)
                tokenValue = formatToken(tokenValue, modifier);
            if (compareWith !== undefined && compareWith !== null) {
                const values = compareWith.split(',');
                const match = values.includes(tokenValue);
                result = operator === '!=' ? !match : match;
            }
            else {
                result = tokenValue !== "" && tokenValue !== null && tokenValue !== undefined;
            }
        }
        return negated ? !result : result;
    };
    const OPEN_IF = "{{#if";
    const OPEN_ELSEIF = "{{#elseif";
    const ELSE = "{{#else}}";
    const CLOSE = "{{/if}}";
    // Reduce innermost blocks repeatedly
    let firstOpen = template.indexOf(OPEN_IF);
    while (firstOpen > -1) {
        firstOpen = template.indexOf(OPEN_IF);
        if (firstOpen === -1)
            break;
        // Find innermost {{#if ...}} ... {{/if}} using a stack
        let i = 0;
        const stack = [];
        let foundPair = null;
        while (i < template.length) {
            const nextOpen = template.indexOf(OPEN_IF, i);
            const nextClose = template.indexOf(CLOSE, i);
            if (nextOpen !== -1 && (nextOpen < nextClose || nextClose === -1)) {
                const tagEnd = template.indexOf("}}", nextOpen);
                if (tagEnd === -1)
                    break; // malformed
                stack.push({ openStart: nextOpen, openEnd: tagEnd + 2 });
                i = tagEnd + 2;
            }
            else if (nextClose !== -1) {
                const opener = stack.pop();
                if (!opener) {
                    // Unbalanced; skip this close and continue
                    i = nextClose + CLOSE.length;
                    continue;
                }
                foundPair = {
                    openStart: opener.openStart,
                    openEnd: opener.openEnd,
                    closeStart: nextClose,
                    closeEnd: nextClose + CLOSE.length
                };
                break; // innermost found
            }
            else {
                break;
            }
        }
        if (!foundPair)
            break;
        const { openStart, openEnd, closeStart, closeEnd } = foundPair;
        // Parse head {{#if ...}}
        const openTag = template.slice(openStart, openEnd);
        const head = parseCondTag(openTag);
        if (!head) {
            // malformed opener; drop the tag to avoid infinite loop
            template = template.slice(0, openStart) + template.slice(openEnd);
            continue;
        }
        // Inner content of this block (between head and close)
        const inner = template.slice(openEnd, closeStart);
        const segments = [{ cond: head, content: "" }];
        let elseContent = "";
        let cursor = 0;
        let hitElse = false;
        const findNextMarker = (from) => {
            const idxElseIf = inner.indexOf(OPEN_ELSEIF, from);
            const idxElse = inner.indexOf(ELSE, from);
            if (idxElseIf === -1 && idxElse === -1)
                return { type: null, idx: -1 };
            if (idxElseIf !== -1 && (idxElseIf < idxElse || idxElse === -1)) {
                return { type: "elseif", idx: idxElseIf };
            }
            return { type: "else", idx: idxElse };
        };
        while (cursor <= inner.length) {
            const { type, idx } = findNextMarker(cursor);
            if (!type) {
                // No more markers
                if (hitElse)
                    elseContent += inner.slice(cursor);
                else
                    segments[segments.length - 1].content += inner.slice(cursor);
                break;
            }
            if (type === "elseif") {
                // Content up to elseif belongs to current segment
                segments[segments.length - 1].content += inner.slice(cursor, idx);
                // Read elseif tag
                const tagEnd = inner.indexOf("}}", idx);
                if (tagEnd === -1)
                    break; // malformed
                const elseifTag = inner.slice(idx, tagEnd + 2);
                const parsedElseIf = parseCondTag(elseifTag);
                segments.push({ cond: parsedElseIf ?? undefined, content: "" });
                cursor = tagEnd + 2;
            }
            else {
                // ELSE
                segments[segments.length - 1].content += inner.slice(cursor, idx);
                cursor = idx + ELSE.length;
                hitElse = true;
                elseContent = inner.slice(cursor); // rest belongs to else
                break;
            }
        }
        // Evaluate segments in order (first truthy wins), otherwise else (if present)
        let replacement = "";
        const winner = segments.find(s => s.cond && evalCondition(s.cond.negated, s.cond.tokenName, s.cond.modifier, s.cond.operator, s.cond.compareWith));
        if (winner)
            replacement = winner.content;
        else if (hitElse)
            replacement = elseContent;
        // Replace whole block
        template = template.slice(0, openStart) + replacement + template.slice(closeEnd);
    }
    return template;
}
function parseModifierChain(modifierChain) {
    if (!modifierChain)
        return [];
    return modifierChain.split('|').map(segment => {
        const match = /^([^(]+)(?:\(([^)]*)\))?$/.exec(segment.trim());
        if (!match)
            return { name: segment.trim(), params: [] };
        return {
            name: match[1].trim(),
            params: match[2] ? match[2].split(',').map(p => p.trim()) : []
        };
    });
}
function applyModifiers(tokenValue, modifiers) {
    for (const mod of modifiers) {
        tokenValue = formatToken(tokenValue, mod.name, mod.params);
    }
    return tokenValue;
}
/**
 * Replaces tokens in a template string with values from tokenValues.
 * template format: 'My Branch is {{branch}}'
 * tokenValues format: {branch: 'HEADOFFICE'}
 * @param {string} template - The template string containing tokens to be replaced.
 * @param {Object} tokenValues - An object containing key-value pairs where the keys represent tokens and the values represent their replacements.
 * @param options
 * @returns {string} - The updated template string with tokens replaced by their respective values.
 */
export function replaceTokens(template, tokenValues, options = {}) {
    template = parseConditionals(template, options, tokenValues);
    // Check if there are any potential global tokens before using regex
    if (template.indexOf('{{') > -1) {
        // Use a single regex pass to match all tokens of the form {{...}}.
        // This regex splits the token into its "name" and optional modifier parts.
        const tokenRegex = /{{([^}]+)}}/g;
        template = template.replace(tokenRegex, (match, tokenTag) => {
            const colonIndex = tokenTag.indexOf(':');
            const tokenNamePart = colonIndex > -1 ? tokenTag.substring(0, colonIndex) : tokenTag;
            const modifierChain = colonIndex > -1 ? tokenTag.substring(colonIndex + 1) : '';
            const modifiers = parseModifierChain(modifierChain);
            let tokenValue;
            // 1. Prioritize tokenValues if provided.
            if (!options.recordTokensOnly && tokenValues && Reflect.has(tokenValues, tokenNamePart)) {
                tokenValue = tokenValues[tokenNamePart] || ''; // handle null so we dont get 'null'
            }
            else if (options.records && tokenNamePart.indexOf('.')) {
                /* we can add this eventually if we decide we want to support nested properties
                and just remove the "options.records" portion of the above if statement
                if (tokenValues) {
                    const deep = getDeepProperty(tokenValues, tokenNamePart);
                    if (deep !== undefined) tokenValue = String(deep);
                }*/
                if (typeof tokenValue === 'undefined' && options.records) {
                    const dotIndex = tokenNamePart.indexOf('.');
                    const recordName = tokenNamePart.substring(0, dotIndex);
                    const fieldName = tokenNamePart.substring(dotIndex + 1);
                    const record = options.records[recordName];
                    if (record && Reflect.has(record.data, fieldName)) {
                        tokenValue = record.data[fieldName] || '';
                    }
                }
            } /*else if (typeof tokenValue === 'undefined' && options.records) {
                // 2. If not found, and if a records map is provided,
                // check if the token follows the record.field pattern.
                const dotIndex = tokenNamePart.indexOf('.');
                if (dotIndex > -1) {
                    const recordName = tokenNamePart.substring(0, dotIndex);
                    const fieldName = tokenNamePart.substring(dotIndex + 1);
                    const record = options.records[recordName];
                    if (record && Reflect.has(record.data, fieldName)) {
                        tokenValue = record.data[fieldName] || '';
                    }
                }
            }*/
            if (tokenNamePart === 'NOW') {
                tokenValue = DateTimeManager.now('SYSTEM', 'string');
            }
            else if (tokenNamePart.startsWith('NOW_')) {
                tokenValue = DateTimeManager.now(tokenNamePart.substring(4), 'string');
            }
            // 3. Check for Global tokens
            if (!options.recordTokensOnly && typeof tokenValue === 'undefined') {
                tokenValue = TOKEN(`{{${tokenNamePart}}}`);
            }
            // 4. If still undefined, check the parseTokenCallback.
            if (typeof tokenValue === 'undefined' && options.parseTokenCallback) {
                tokenValue = options.parseTokenCallback(tokenNamePart, modifierChain || '');
            }
            // If still undefined, and we're not keeping missing tokens, default to empty string.
            if (typeof tokenValue === 'undefined' && !options.keepMissingTokens) {
                tokenValue = '';
            }
            // If modifiers are present, format the token.
            if (tokenValue !== undefined && modifiers.length > 0) {
                if (modifiers[0].name === 'tokenLiteral' || modifiers[0].name === 'caption') {
                    tokenValue = tokenNamePart;
                }
                tokenValue = applyModifiers(tokenValue, modifiers);
            }
            // URL encode if required.
            if (tokenValue !== undefined && options.urlEncode && !modifiers.some(m => m.name === 'encoded')) {
                tokenValue = encodeURIComponent(tokenValue);
            }
            return tokenValue !== undefined ? tokenValue : match;
        });
    }
    return template;
}
export function formatToken(tokenValue, modifier, params = []) {
    if (tokenValue) {
        switch (modifier) {
            case 'dateFormat':
                tokenValue = DateTimeManager.formatDate(tokenValue, 'display');
                break;
            case 'datetimeFormat':
                tokenValue = DateTimeManager.formatDateTime(tokenValue, 'display');
                break;
            case 'plaintext':
                tokenValue = htmlToPlaintext(tokenValue);
                break;
            case 'html':
                tokenValue = plaintextToHTML(tokenValue);
                break;
            case 'tokenLiteral':
                tokenValue = `{{${tokenValue}}}`;
                break;
            case 'tokenEncoded':
                tokenValue = encodeURIComponent(tokenValue);
                break;
            case 'round0':
                tokenValue = tokenValue ? Number(tokenValue).toFixed(0) : tokenValue;
                break;
            case 'caption':
                tokenValue = DIContainer.getInstance('CaptionManager').getCaption(tokenValue);
                break;
            case 'dateAdd': {
                const amount = parseFloat(params[0] || '0');
                const unit = (params[1] || 'days');
                if (!isNaN(amount))
                    tokenValue = DateTimeManager.dateAdd(tokenValue, unit, amount);
                break;
            }
            case 'round': {
                const decimals = parseInt(params[0] || '0', 10);
                if (!isNaN(decimals))
                    tokenValue = Number(tokenValue).toFixed(decimals);
                break;
            }
        }
    }
    switch (modifier) {
        case 'ifNull':
            if (tokenValue === null || tokenValue === undefined)
                tokenValue = params[0] || '';
            break;
        case 'ifEmpty':
            if (!tokenValue)
                tokenValue = params[0] || '';
            break;
        case 'stepUp': {
            const value = parseFloat(tokenValue) || 0;
            const increment = parseFloat(params[0] || '1');
            if (isNaN(increment) || increment === 0)
                break;
            const stepsUp = Math.floor(value / increment);
            let result = (stepsUp + 1) * increment;
            if (params[1] !== undefined && params[1] !== '')
                result = Math.min(result, parseFloat(params[1]));
            tokenValue = String(parseFloat(result.toFixed(10)));
            break;
        }
        case 'stepDown': {
            const value = parseFloat(tokenValue) || 0;
            const increment = parseFloat(params[0] || '1');
            if (isNaN(increment) || increment === 0)
                break;
            const stepsDown = Math.ceil(value / increment);
            let result = (stepsDown - 1) * increment;
            if (params[1] !== undefined && params[1] !== '')
                result = Math.max(result, parseFloat(params[1]));
            tokenValue = String(parseFloat(result.toFixed(10)));
            break;
        }
    }
    return tokenValue;
}
export function createElement(tag, classNames = [], attributes = {}) {
    const element = document.createElement(tag);
    element.classList.add(...classNames);
    for (const [key, value] of Object.entries(attributes)) {
        element.setAttribute(key, value);
    }
    return element;
}
